'use client'

import Image from 'next/image';
import { useState, useRef, useEffect } from 'react';

import CtaBanner from "@/components/cta-banner/cta-banner";
import StoryOverlay from "@/components/story-overlay/story-overlay";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";
import useImageLoad from '@/hooks/useImageLoad';
import useSwipeNavigation from "@/hooks/useSwipeNavigation";

export default function StoryLayout({ stories, slug, theme, content }) {
  const { isRouteChanging, previousStoryColor, setPreviousStoryColor, currentTransitionName } = useApp();
  const isSlideTransition = currentTransitionName === 'slide-left' || currentTransitionName === 'slide-right';
  const bgRef = useRef(null);

  const [isUiMounted, setIsArrowNavMounted] = useState(false);
  const closeButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  /**
   * Gets the previous and next story routes with wrap-around navigation.
   * If current story is first, previous wraps to last. If current is last, next wraps to first.
   * @param {Array} stories - Array of story objects to navigate through
   * @param {Function} buildRoute - Function that takes a story and returns its route URL
   * @returns {{ previousRoute: string, nextRoute: string }}
   */
  const getAdjacentStories = (buildRoute) => {
    const currentIndex = stories.findIndex(story => story.slug === slug);
    const previousIndex = currentIndex === 0 ? stories.length - 1 : currentIndex - 1;
    const nextIndex = currentIndex === stories.length - 1 ? 0 : currentIndex + 1;

    return {
      previousRoute: buildRoute(stories[previousIndex]),
      nextRoute: buildRoute(stories[nextIndex]),
    };
  };

  const buildRoute = (story) => `${STORIES_BASE_PATH}/250-stories/${story.slug}`;
  const { previousRoute: previousStoryRoute, nextRoute: nextStoryRoute } = getAdjacentStories(buildRoute);

  // Fade in arrow navigation after mount
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsArrowNavMounted(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  // Keyboard navigation: ESC to close, left/right arrows to navigate stories
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
      case 'Escape':
        closeButtonRef.current?.click();
        break;
      case 'ArrowLeft':
        prevButtonRef.current?.click();
        break;
      case 'ArrowRight':
        nextButtonRef.current?.click();
        break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Touch swipe navigation: swipe left/right to navigate stories
  useSwipeNavigation({
    onSwipeLeft: () => nextButtonRef.current?.click(),
    onSwipeRight: () => prevButtonRef.current?.click(),
  });

  /** Store current theme color before navigating to another story */
  const handleStoryNavigation = () => {
    // Get the computed color value from the CSS variable
    const computedColor = getComputedStyle(bgRef.current).backgroundColor;
    setPreviousStoryColor(computedColor);
  };

  const {imageRef, onLoad} = useImageLoad()

  return (
    <StoryOverlay
      theme={theme}
      previousRoute={previousStoryRoute}
      nextRoute={nextStoryRoute}
      closeHref={`${STORIES_BASE_PATH}/250-stories`}
      onNavigate={handleStoryNavigation}
      isVisible={isUiMounted && !(isRouteChanging && isSlideTransition)}
      showCloseButton={true}
      previousColor={previousStoryColor}
      bgRef={bgRef}
      prevRef={prevButtonRef}
      nextRef={nextButtonRef}
      closeRef={closeButtonRef}
      hasLightControls={true}
      hasLightCloseCta={true}
    >
      <main className="grid-9 items-center text-main-light mt-23 lg:mt-0">
        <div className="relative col-span-full lg:col-span-4">
          <Image unoptimized={true} className="w-full h-auto opacity-0" {...content.image} priority={true} ref={imageRef} onLoad={onLoad}/>
          {content.disclaimer &&
            (<div className="body-3-sans-small mt-6 text-center text-white/60 xl:body-3-sans" dangerouslySetInnerHTML={{__html: content.disclaimer}}></div>)
          }
        </div>

        <article className="col-span-full mt-7 lg:[grid-column:6/-1] lg:mt-0">
          <h1 className="mt-4 heading-2-small text-pretty xl:heading-2">{content.title}</h1>
          <div className="mt-4 body-1-sans-small flex flex-wrap items-center gap-x-3 gap-y-1 lg:gap-x-3.5 xl:body-2-sans-bold">
            <span className="shrink-0">{content.name}</span>
            <span className="shrink-0 w-[5px] h-[5px] rounded-full bg-current" aria-hidden="true" />
            <span className="shrink-0">{content.location}</span>
          </div>
          <p className="mt-9 body-1-small text-pretty xl:body-1" dangerouslySetInnerHTML={{__html: content.text}}></p>

          <CtaBanner isDark={true} />
        </article>
      </main>
    </StoryOverlay>
  );
}
